'use client';

import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { Mail, ArrowRight, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

export default function RecoveryPage() {
  const locale = useLocale();
  const t = useTranslations('Recovery');
  const searchParams = useSearchParams();
  const linkExpired = searchParams.get('error') === 'link_expired';

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(linkExpired ? t('linkExpired') : null);

  const handleRecover = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const response = await fetch('/api/auth/recovery/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          locale,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        const message = data?.error_code === 'RECOVERY_RATE_LIMITED'
          ? t('rateLimited')
          : t('errorSending');
        throw new Error(message);
      }

      setMessage(t('successMessage'));
    } catch (err) {
      setError(err.message || t('errorSending'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#020617] p-4 font-sans">
      <div className="w-full max-w-md bg-slate-900/80 backdrop-blur-md p-8 rounded-2xl border border-cyan-500/30 shadow-[0_0_40px_rgba(6,182,212,0.1)]">
        
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">{t('pageTitle')}</h1>
          <p className="text-slate-400 text-sm">{t('pageSubtitle')}</p>
        </div>

        {!message ? (
          <form onSubmit={handleRecover} className="space-y-6">
            <div>
              <label className="block text-cyan-100 text-sm font-bold mb-2 ml-1">{t('emailLabel')}</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-cyan-500/60" />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-[#0F172A] text-white border border-slate-700 focus:border-cyan-400 outline-none placeholder:text-slate-600"
                  placeholder={t('emailPlaceholder')}
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-950/30 text-red-400 p-3 rounded-lg text-sm border border-red-500/20">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold shadow-lg shadow-cyan-500/20 hover:scale-[1.02] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin w-5 h-5" /> : <>{t('sendButton')} <ArrowRight size={18} /></>}
            </button>
          </form>
        ) : (
          <div className="text-center py-6 animate-in zoom-in">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-500/50">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-white font-bold text-lg mb-2">{t('successTitle')}</h3>
            <p className="text-slate-400 text-sm mb-6">{message}</p>
          </div>
        )}
      </div>
    </div>
  );
}


